import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Tag } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { orgFetch } from '@/lib/org-api'
import { useAuthStore } from '@/lib/auth-store'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { DEPARTMENTS, DEPARTMENT_BY_VALUE, type Department } from '@/lib/departments'

interface OrgUserRow {
  orgUserId: string
  role: 'owner' | 'admin' | 'ops'
  joinedAt: string
  departments: Department[]
  user: {
    id: string
    email: string
    displayName: string
    accountType: string
    lastLoginAt: string | null
  }
}

const ROLE_COLOUR: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  ops: 'bg-slate-100 text-slate-700',
}

export function RosterUsersPage() {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [inviting, setInviting] = useState(false)
  const [editingDeptsFor, setEditingDeptsFor] = useState<string | null>(null)

  const { data, error, isLoading } = useQuery<OrgUserRow[], ApiClientError>({
    queryKey: ['org-users'],
    queryFn: () => orgFetch<OrgUserRow[]>('/users'),
    enabled: !!user?.organisationId,
  })

  async function removeUser(targetUserId: string) {
    if (!confirm('Remove this user from the organisation?')) return
    try {
      await orgFetch(`/users/${targetUserId}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['org-users'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    }
  }

  if (!user?.organisationId) {
    return (
      <div>
        <PageHeader title="Org users" />
        <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
          You're not part of an organisation.
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Org users"
        subtitle="People with cross-artist access to your organisation."
        actions={
          <button
            type="button"
            onClick={() => setInviting((i) => !i)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            {inviting ? 'Cancel' : 'Invite user'}
          </button>
        }
      />

      {inviting && (
        <InviteForm
          onInvited={() => {
            setInviting(false)
            qc.invalidateQueries({ queryKey: ['org-users'] })
          }}
        />
      )}

      <AsyncBoundary isLoading={isLoading} error={error}>
        <DataTable
          rows={data ?? []}
          rowKey={(r) => r.orgUserId}
          emptyMessage="No org users yet."
          columns={[
            { key: 'name', header: 'Name', render: (r) => r.user.displayName },
            { key: 'email', header: 'Email', render: (r) => r.user.email },
            {
              key: 'role',
              header: 'Role',
              render: (r) => (
                <span
                  className={`inline-flex rounded px-2 py-0.5 text-xs ${ROLE_COLOUR[r.role]}`}
                >
                  {r.role}
                </span>
              ),
            },
            {
              key: 'departments',
              header: 'Departments',
              render: (r) => (
                <div className="flex flex-wrap gap-1">
                  {r.departments.length === 0 ? (
                    <span className="text-xs italic text-ovation-muted">— none —</span>
                  ) : (
                    r.departments.map((d) => (
                      <span
                        key={d}
                        className="inline-flex items-center gap-1 rounded bg-ovation-ink/5 px-1.5 py-0.5 text-[10px] text-ovation-ink"
                      >
                        {DEPARTMENT_BY_VALUE[d]?.label ?? d}
                      </span>
                    ))
                  )}
                  <button
                    type="button"
                    onClick={() => setEditingDeptsFor(r.user.id)}
                    className="inline-flex items-center gap-1 rounded border border-dashed border-ovation-ink/20 px-1.5 py-0.5 text-[10px] text-ovation-muted hover:bg-ovation-ink/5 hover:text-ovation-ink"
                    title="Edit departments"
                  >
                    <Tag className="h-3 w-3" />
                    Edit
                  </button>
                </div>
              ),
            },
            {
              key: 'lastLogin',
              header: 'Last login',
              render: (r) =>
                r.user.lastLoginAt
                  ? new Date(r.user.lastLoginAt).toLocaleDateString()
                  : 'Never',
            },
            {
              key: 'actions',
              header: '',
              align: 'right',
              render: (r) => (
                <button
                  type="button"
                  onClick={() => removeUser(r.user.id)}
                  className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                  disabled={r.user.id === user?.userId}
                  title={
                    r.user.id === user?.userId
                      ? 'You cannot remove yourself'
                      : 'Remove from org'
                  }
                >
                  <Trash2 className="h-3 w-3" />
                  Remove
                </button>
              ),
            },
          ]}
        />
      </AsyncBoundary>

      {editingDeptsFor && data && (
        <DepartmentEditor
          row={data.find((r) => r.user.id === editingDeptsFor)!}
          onClose={() => setEditingDeptsFor(null)}
          onSaved={() => {
            setEditingDeptsFor(null)
            qc.invalidateQueries({ queryKey: ['org-users'] })
          }}
        />
      )}
    </div>
  )
}

function DepartmentEditor({
  row,
  onClose,
  onSaved,
}: {
  row: OrgUserRow
  onClose: () => void
  onSaved: () => void
}) {
  const [selected, setSelected] = useState<Set<Department>>(new Set(row.departments))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  function toggle(d: Department) {
    const next = new Set(selected)
    if (next.has(d)) next.delete(d)
    else next.add(d)
    setSelected(next)
  }

  async function save() {
    setSaving(true)
    setErr(null)
    try {
      await orgFetch(`/users/${row.user.id}/departments`, {
        method: 'PUT',
        body: { departments: [...selected] },
      })
      onSaved()
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 font-display text-lg">Departments for {row.user.displayName}</h3>
        <p className="mb-4 text-xs text-ovation-muted">
          A department member is the first port of call for any inbound email routed to that
          department.
        </p>
        <ul className="space-y-1.5">
          {DEPARTMENTS.map((d) => (
            <li key={d.value} className="flex items-start gap-2">
              <input
                id={`dept-${d.value}`}
                type="checkbox"
                checked={selected.has(d.value)}
                onChange={() => toggle(d.value)}
                className="mt-0.5"
              />
              <label htmlFor={`dept-${d.value}`} className="flex-1 cursor-pointer">
                <div className="text-sm font-medium">{d.label}</div>
                <div className="text-[11px] text-ovation-muted">{d.description}</div>
              </label>
            </li>
          ))}
        </ul>
        {err && <p className="mt-3 text-xs text-red-700">{err}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm hover:bg-ovation-ink/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded-md bg-ovation-ink px-3 py-1.5 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function InviteForm({ onInvited }: { onInvited: () => void }) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'owner' | 'admin' | 'ops'>('admin')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      await orgFetch('/users', {
        method: 'POST',
        body: { email, role },
      })
      onInvited()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-ovation-ink/10 bg-white p-4"
    >
      <div className="flex-1 min-w-[200px]">
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
          Email
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="user@example.com"
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
          Role
        </label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as 'owner' | 'admin' | 'ops')}
          className="rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        >
          <option value="admin">Admin</option>
          <option value="ops">Ops</option>
          <option value="owner">Owner</option>
        </select>
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
      >
        {submitting ? 'Inviting…' : 'Invite'}
      </button>
      {error && (
        <div className="basis-full text-sm text-red-700">{error}</div>
      )}
      <p className="basis-full text-xs text-ovation-muted">
        The user must already have an Ovation account. Email-based invites
        ship in Phase F.
      </p>
    </form>
  )
}
